import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal } from '@mui/material';
import { Close } from '@mui/icons-material';
import styled from 'styled-components';

const ZOOM_SCALE = 2.5;

const ImageModal = ({ open, close, image }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [orientation, setOrientation] = useState('landscape');
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  const resetZoom = useCallback(() => {
    setIsZoomed(false);
    setPosition({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    resetZoom();
  }, [open, resetZoom]);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setOrientation(img.width > img.height ? 'landscape' : 'portrait');
    };
    img.src = image;
  }, [image]);

  const handleImageClick = useCallback((e) => {
    if (isZoomed) {
      resetZoom();
    } else {
      setIsZoomed(true);
      updateZoomPosition(e);
    }
  }, [isZoomed, resetZoom]);

  const updateZoomPosition = useCallback((e) => {
    if (!imageRef.current || !containerRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();

    const imageWidth = rect.width;
    const imageHeight = rect.height;
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    const xRatio = (e.clientX - containerRect.left) / containerWidth;
    const yRatio = (e.clientY - containerRect.top) / containerHeight;

    const x = xRatio * imageWidth;
    const y = yRatio * imageHeight;

    setPosition({
      x: Math.max(0, Math.min(100, (x / imageWidth) * 100)),
      y: Math.max(0, Math.min(100, (y / imageHeight) * 100))
    });
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (isZoomed) {
      updateZoomPosition(e);
    }
  }, [isZoomed, updateZoomPosition]);

  return (
    <Modal
      open={open}
      onClose={close}
      className='image-modal-zoom'
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
      }}
    >
      <ModalContent orientation={orientation}>
        <CloseButton onClick={close}>
          <Close />
        </CloseButton>
        <ImageWrapper
          orientation={orientation}
          onClick={handleImageClick}
          onMouseMove={handleMouseMove}
          ref={containerRef}
          $isZoomed={isZoomed}
        >
          <StyledImage
            ref={imageRef}
            src={image}
            alt="Modal content"
            $isZoomed={isZoomed}
            position={position}
            draggable={false}
          />
        </ImageWrapper>
        <ZoomInstructions>
          {isZoomed ? 'Click to zoom out' : 'Click to zoom in'}
        </ZoomInstructions>
      </ModalContent>
    </Modal>
  );
};

export default ImageModal;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 20px;
  position: relative;
  width: ${props => props.orientation === 'landscape' ? '90%' : '60%'};
  height: ${props => props.orientation === 'landscape' ? '80%' : '90%'};
  max-width: 1200px;
  max-height: 800px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  @media (max-width: 426px) {
   width: ${props => props.orientation === 'landscape' ? '90%' : '80%'};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgb(204, 80, 71); 
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1;
`;

const ImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: scroll;
  cursor: ${props => props.$isZoomed ? 'zoom-out' : 'zoom-in'};
`;

const StyledImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease-out;
  transform-origin: top left;
  transform: ${props => props.$isZoomed 
    ? `scale(${ZOOM_SCALE}) translate(${-props.position.x * (ZOOM_SCALE - 1) / ZOOM_SCALE}%, ${-props.position.y * (ZOOM_SCALE - 1) / ZOOM_SCALE}%)`
    : 'none'};
  pointer-events: ${props => props.$isZoomed ? 'none' : 'auto'};
`;

const ZoomInstructions = styled.div`
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 14px;
`;